import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  writeBatch,
  serverTimestamp,
  Timestamp,
  type DocumentData,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { DiaryEntry, DiaryEntryInput } from '@/types'

const COL = 'lb_diary_entries'

function tsToDate(v: unknown): Date | undefined {
  if (!v) return undefined
  if (v instanceof Timestamp) return v.toDate()
  if (v instanceof Date) return v
  return undefined
}

function mapEntry(id: string, data: DocumentData): DiaryEntry {
  return {
    ...(data as DiaryEntry),
    id,
    timestamp: tsToDate(data.timestamp) ?? new Date(),
    createdAt: tsToDate(data.createdAt),
    updatedAt: tsToDate(data.updatedAt),
  }
}

function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v
  }
  return out as T
}

export const DiaryService = {
  /** Real-time entries for a single date (needs the userId+date+timestamp index). */
  subscribeToEntriesForDate(
    userId: string,
    date: string,
    onUpdate: (entries: DiaryEntry[]) => void,
    onError?: (e: Error) => void,
  ): () => void {
    const q = query(
      collection(db, COL),
      where('userId', '==', userId),
      where('date', '==', date),
      orderBy('timestamp', 'asc'),
    )
    return onSnapshot(
      q,
      (snap) => onUpdate(snap.docs.map((d) => mapEntry(d.id, d.data()))),
      (err) => onError?.(err),
    )
  },

  /** One date's entries, oldest first — mobile getEntriesForDate (same index as the subscription). */
  async getEntriesForDate(userId: string, date: string): Promise<DiaryEntry[]> {
    const q = query(
      collection(db, COL),
      where('userId', '==', userId),
      where('date', '==', date),
      orderBy('timestamp', 'asc'),
    )
    const snap = await getDocs(q)
    return snap.docs.map((d) => mapEntry(d.id, d.data()))
  },

  async createEntry(userId: string, input: DiaryEntryInput): Promise<string> {
    const payload = stripUndefined({
      ...input,
      userId,
      timestamp: input.timestamp ? Timestamp.fromDate(input.timestamp) : serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    const ref = await addDoc(collection(db, COL), payload)
    return ref.id
  },

  /**
   * Create several entries in ONE batch — mobile createEntries (saved-foods
   * multi-select). Returns the new ids in input order.
   */
  async createEntries(userId: string, inputs: DiaryEntryInput[]): Promise<string[]> {
    if (inputs.length === 0) return []
    const batch = writeBatch(db)
    const refs = inputs.map(() => doc(collection(db, COL)))
    inputs.forEach((input, i) => {
      batch.set(
        refs[i],
        stripUndefined({
          ...input,
          userId,
          timestamp: input.timestamp ? Timestamp.fromDate(input.timestamp) : serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }),
      )
    })
    await batch.commit()
    return refs.map((r) => r.id)
  },

  async updateEntry(id: string, partial: Partial<DiaryEntry>): Promise<void> {
    const { id: _omit, timestamp, createdAt, ...rest } = partial
    void _omit
    void timestamp
    void createdAt
    await updateDoc(doc(db, COL, id), stripUndefined({ ...rest, updatedAt: serverTimestamp() }))
  },

  async deleteEntry(id: string): Promise<void> {
    await deleteDoc(doc(db, COL, id))
  },

  /**
   * Copy a single entry to another date (or same date with a new meal type) —
   * mirrors mobile copySingleEntry: keeps the original source, fresh timestamps.
   */
  async copySingleEntry(
    entry: DiaryEntry,
    targetDate: string,
    targetMealType?: DiaryEntry['mealType'],
  ): Promise<string> {
    if (entry.source === 'healthkit') throw new Error('Cannot copy HealthKit entries')
    const { id, createdAt, updatedAt, timestamp, ...entryData } = entry
    void id
    void createdAt
    void updatedAt
    void timestamp
    const ref = await addDoc(
      collection(db, COL),
      stripUndefined({
        ...entryData,
        date: targetDate,
        mealType: targetMealType || entry.mealType,
        timestamp: Timestamp.fromDate(new Date()),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }),
    )
    return ref.id
  },

  /** Copy specific entries to another date in one batch — mirrors mobile copyMultipleEntries. */
  async copyMultipleEntries(entriesToCopy: DiaryEntry[], targetDate: string): Promise<number> {
    const batch = writeBatch(db)
    let count = 0
    for (const entry of entriesToCopy) {
      if (entry.source === 'healthkit') continue
      // Mobile guard: copying onto the same day must be a no-op, not a duplicate.
      if (entry.date === targetDate) continue
      const { id, createdAt, updatedAt, timestamp, ...entryData } = entry
      void id
      void createdAt
      void updatedAt
      void timestamp
      const ref = doc(collection(db, COL))
      batch.set(
        ref,
        stripUndefined({
          ...entryData,
          date: targetDate,
          timestamp: Timestamp.fromDate(new Date()),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }),
      )
      count++
    }
    if (count > 0) await batch.commit()
    return count
  },

  /** Copy all of one day's entries onto another date (skips HealthKit imports). */
  async copyEntriesForDate(
    userId: string,
    sourceDate: string,
    targetDate: string,
    includeFluids = true,
  ): Promise<number> {
    const q = query(
      collection(db, COL),
      where('userId', '==', userId),
      where('date', '==', sourceDate),
    )
    const snap = await getDocs(q)
    const batch = writeBatch(db)
    let count = 0
    for (const d of snap.docs) {
      const data = d.data() as DiaryEntry
      if (data.source === 'healthkit') continue
      if (!includeFluids && data.source === 'fluid') continue
      const { id, createdAt, updatedAt, timestamp, ...rest } = data
      void id
      void createdAt
      void updatedAt
      void timestamp
      const ref = doc(collection(db, COL))
      batch.set(
        ref,
        stripUndefined({
          ...rest,
          userId,
          date: targetDate,
          // Mobile preserves the ORIGINAL source (a copied fluid entry must
          // still be source:'fluid' so include-water filters keep working).

          timestamp: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }),
      )
      count++
    }
    if (count > 0) await batch.commit()
    return count
  },
}
